import {
  Component, OnDestroy, OnInit, Inject,
} from '@angular/core';
import {
  FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors,
} from '@angular/forms';
import { Subscription } from 'rxjs';
// @ts-ignore - eslint resolver may lag on newly added file during conversion
import { UnifiedRegistrationApiService } from '../unified-registration.api.service';
import { GoogleMapsLoaderService } from '../../services/google-maps-loader.service';
import { EMAIL_REGEX, NAME_REGEX } from '../../../constants';

type BirthDate = { day: number | null; month: number | null; year: number | null };

function ageValidator(control: AbstractControl): ValidationErrors | null {
  const birthDay = control.get('birthDay')?.value;
  const birthMonth = control.get('birthMonth')?.value;
  const birthYear = control.get('birthYear')?.value;

  if (!birthDay || !birthMonth || !birthYear) {
    return null;
  }

  const day = Number(birthDay);
  const month = Number(birthMonth);
  const year = Number(birthYear);

  const birthDate = new Date(year, month - 1, day);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  // Adjust age if birthday hasn't occurred this year yet
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  if (age < 18) {
    return { underAge: true };
  }

  return null;
}

function trimmedMinLengthValidator(minLength: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const trimmedValue = control.value.toString().trim();
    if (trimmedValue.length < minLength) {
      return {
        trimmedMinlength: {
          requiredLength: minLength,
          actualLength: trimmedValue.length,
        },
      };
    }

    return null;
  };
}

declare global {
  interface Window {
    grecaptcha: any;
  }
}

@Component({
  selector: 'ut-unified-registration-form',
  templateUrl: './form.component.html',
  standalone: false,
})
export class UnifiedRegistrationFormComponent implements OnInit, OnDestroy {
  form: FormGroup;
  submitting = false;
  user: any | null = null;
  customRegistrationId: string | null = null;
  recaptchaSiteKey: string | null = null;

  // Custom touched state for terms checkbox to override Angular's default behavior
  termsGroupTouched = false;
  // Track if terms checkbox was ever checked to trigger validation on uncheck
  termsWasChecked = false;

  countries: Array<{ id: number; name: string; code?: string }> = [];
  states: Array<{ id: number; name: string }> = [];
  months: Array<{ id: number; name: string }> = [];
  days: number[] = [];
  years: number[] = [];

  private subs: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private api: UnifiedRegistrationApiService,
    private googleMapsLoader: GoogleMapsLoaderService,
    @Inject('$timeout') private $timeout: any,
    @Inject('$transition$') private $transition$: any,
    @Inject('$state') private $state: any,
    @Inject('CustomRegistrationService') private CustomRegistrationService: any,
    @Inject('ConfigService') private ConfigService: any,
    @Inject('StaticValuesService') private StaticValuesService: any,
    @Inject('$window') private $window: any,
  ) {
    this.form = this.fb.group({
      firstName: [
        '',
        [
          Validators.required,
          trimmedMinLengthValidator(2),
          Validators.maxLength(50),
          Validators.pattern(NAME_REGEX),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          trimmedMinLengthValidator(2),
          Validators.maxLength(50),
          Validators.pattern(NAME_REGEX),
        ],
      ],
      email: ['', [Validators.required, Validators.pattern(EMAIL_REGEX)]],
      countryId: ['', [Validators.required]],
      stateId: [''],
      birthDay: ['', [Validators.required]],
      birthMonth: ['', [Validators.required]],
      birthYear: ['', [Validators.required]],
      newsletterOptIn: [false],
      termsAccepted: [false, [Validators.requiredTrue]],
    }, { validators: ageValidator });
  }

  ngOnInit(): void {
    this.customRegistrationId = this.$transition$.params().customRegistrationId || null;

    this.subs.push(this.api.getCountries().subscribe((countries) => (this.countries = countries)));
    this.subs.push(
      this.form.get('countryId')!.valueChanges.subscribe((countryId) => this.onCountryChange(countryId)),
    );
    const now = new Date();
    this.months = this.api.getMonths(now.getFullYear());
    this.years = this.api.getYears();
    this.updateDaysForMonth();

    this.ConfigService.config().then((response: any) => {
      this.recaptchaSiteKey = response.data.recaptcha_v3_site_key;
    });

    this.autodetectCountry();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private onCountryChange(countryId: number | string | null): void {
    const stateControl = this.form.get('stateId')!;
    // clear any stale selection from the previous country
    stateControl.setValue('');
    stateControl.markAsUntouched();
    stateControl.markAsPristine();
    this.states = [];
    stateControl.clearValidators();
    stateControl.updateValueAndValidity();

    if (!countryId) return;

    this.subs.push(
      this.api.getStates(countryId).subscribe((states) => {
        // guard against an out-of-order response for a country the user has since changed away from
        if (String(this.form.get('countryId')!.value) !== String(countryId)) return;

        this.states = (states || []).slice().sort((a, b) => a.name.localeCompare(b.name));
        if (this.states.length) {
          stateControl.setValidators([Validators.required]);
        } else {
          stateControl.clearValidators();
        }
        // the field is newly appearing/re-validated — don't surface an error until the
        // user actually interacts with it themselves
        stateControl.markAsUntouched();
        stateControl.markAsPristine();
        stateControl.updateValueAndValidity();
      }),
    );
  }

  onMonthYearChange(): void {
    const prevDayRaw = this.form.get('birthDay')!.value as number | string | null;
    const prevDay = prevDayRaw != null && prevDayRaw !== '' ? Number(prevDayRaw) : null;
    this.updateDaysForMonth();
    if (prevDay && this.days.includes(prevDay)) {
      this.form.get('birthDay')!.setValue(prevDay);
    }
  }

  private updateDaysForMonth(): void {
    const yearRaw = this.form.get('birthYear')!.value as number | string | null;
    const monthRaw = this.form.get('birthMonth')!.value as number | string | null;
    const year = yearRaw != null && yearRaw !== '' ? Number(yearRaw) : null;
    const month = monthRaw != null && monthRaw !== '' ? Number(monthRaw) : null;
    this.days = this.api.getDays(month ?? 1, year ?? new Date().getFullYear());
    const dayRaw = this.form.get('birthDay')!.value as number | string | null;
    const day = dayRaw != null && dayRaw !== '' ? Number(dayRaw) : null;
    if (day && !this.days.includes(day)) {
      this.form.get('birthDay')!.setValue('');
    }
  }

  onSubmit(): void {
    this.attemptSubmit();
  }

  private attemptSubmit(retryCount = 0): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting = true;

    // Execute reCAPTCHA v3
    this.executeRecaptcha().then((token: string) => {
      const birth: BirthDate = {
        day: this.form.value.birthDay,
        month: this.form.value.birthMonth,
        year: this.form.value.birthYear,
      };

      const payload = {
        firstName: this.form.value.firstName.trim(),
        lastName: this.form.value.lastName.trim(),
        email: this.form.value.email.trim(),
        countryId: Number(this.form.value.countryId),
        stateId: this.form.value.stateId ? Number(this.form.value.stateId) : undefined,
        birthDate: { day: Number(birth.day), month: Number(birth.month), year: Number(birth.year) },
        newsletterOptIn: !!this.form.value.newsletterOptIn,
        termsAccepted: !!this.form.value.termsAccepted,
        captcha_response: token,
        campaigns: this.api.getUtmCampaignFromLocation(),
        registrationFlowId: this.customRegistrationId
          ? Number(this.customRegistrationId)
          : undefined,
      } as any;

      this.subs.push(
        this.api.createTester(payload).subscribe({
          next: (user) => {
            this.submitting = false;
            this.user = user;

            if (this.customRegistrationId) {
              const userData = {
                ...user,
                firstName: this.form.value.firstName.trim(),
                lastName: this.form.value.lastName.trim(),
                email: this.form.value.email.trim(),
              };

              this.CustomRegistrationService.setRegistrationUserData(userData);

              const params = this.getUtmParams();
              params.customRegistrationId = this.customRegistrationId;
              this.$state.go('signup-success', params);
            } else {
              this.$state.go('signup-success');
            }
          },
          error: () => {
            this.submitting = false;
            this.$state.go('signup-error');
          },
        }),
      );
    }).catch((error) => {
      // Check if error is due to reCAPTCHA script not loaded and retries remain
      const isRecaptchaNotLoaded = error.message === 'reCAPTCHA script not loaded';
      const maxRetries = 3;

      if (isRecaptchaNotLoaded && retryCount < maxRetries) {
        this.submitting = false;
        // Wait 500ms before retrying
        this.$timeout(() => {
          this.attemptSubmit(retryCount + 1);
        }, 500);
      } else {
        this.submitting = false;
        this.$state.go('signup-error');
      }
    });
  }

  private executeRecaptcha(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.recaptchaSiteKey) {
        reject(new Error('reCAPTCHA site key not available'));
        return;
      }

      if (!this.$window.grecaptcha || !this.$window.grecaptcha.execute) {
        reject(new Error('reCAPTCHA script not loaded'));
        return;
      }

      this.$window.grecaptcha.ready(() => {
        this.$window.grecaptcha.execute(this.recaptchaSiteKey, { action: 'unified_registration' })
          .then((token: string) => {
            resolve(token);
          })
          .catch((error: any) => {
            reject(error);
          });
      });
    });
  }

  private getUtmParams(): any {
    const params: any = {};
    const routeParams = this.$transition$.params();

    if (routeParams.utm_source) params.utm_source = routeParams.utm_source;
    if (routeParams.utm_medium) params.utm_medium = routeParams.utm_medium;
    if (routeParams.utm_campaign) params.utm_campaign = routeParams.utm_campaign;
    if (routeParams.utm_content) params.utm_content = routeParams.utm_content;
    if (routeParams.utm_term) params.utm_term = routeParams.utm_term;

    return params;
  }

  private async areCountriesAvailable() {
    let retries = 0;
    const maxRetries = 5;

    while (retries < maxRetries) {
      if (this.countries && this.countries.length > 0) {
        return true;
      }
      retries++;
      await new Promise(resolve => setTimeout(resolve, 500)); // eslint-disable-line
    }

    return false;
  }

  private async autodetectCountry(): Promise<void> {
    try {
      await this.googleMapsLoader.load();
    } catch {
      // without Maps we can't geocode, so leave the country for the user to pick
      return;
    }

    const available = await this.areCountriesAvailable();

    if (!available) {
      return;
    }
    this.StaticValuesService.geolocate().then((geolocate) => {
      const latLng = geolocate.data.location;
      const geocoder = new google.maps.Geocoder();

      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK') {
          const address: any = {};
          results[0].address_components.forEach((el) => {
            if (el.types[0] === 'administrative_area_level_1') {
              address[el.types[0]] = el.long_name;
            } else {
              address[el.types[0]] = el.short_name;
            }
          });

          const selectedCountry = this.countries.find(country => country.code === address.country);
          if (selectedCountry) {
            this.form.get('countryId')?.setValue(selectedCountry.id);
          }
        }
      });
    });
  }

  // UI helpers
  isInvalid(controlName: string): boolean {
    const c = this.form.get(controlName);
    if (!c || !c.invalid) return false;

    // Special handling for termsAccepted to use our custom touched state
    if (controlName === 'termsAccepted') {
      return this.termsGroupTouched;
    }

    return c.touched;
  }

  hasAgeError(): boolean {
    const dayControl = this.form.get('birthDay');
    const monthControl = this.form.get('birthMonth');
    const yearControl = this.form.get('birthYear');

    return dayControl?.value && monthControl?.value && yearControl?.value && this.form.hasError('underAge');
  }

  onCheckboxGroupFocusOut(event: FocusEvent, controlName: string): void {
    // Only mark as touched if focus is leaving the entire checkbox group
    // Check if the new focus target is outside the current checkbox group
    const currentTarget = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement;

    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      // Focus has left the entire checkbox group
      if (controlName === 'termsAccepted') {
        this.termsGroupTouched = true;
      } else {
        this.form.get(controlName)?.markAsTouched();
      }
    }
  }

  toggleCheckbox(controlName: string): void {
    const control = this.form.get(controlName);
    if (control) {
      const currentValue = control.value;
      const newValue = !currentValue;

      control.setValue(newValue);

      // Focus the associated input element
      const inputElement = document.getElementById(controlName) as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
      }

      // Handle special logic for terms checkbox
      if (controlName === 'termsAccepted') {
        // If checkbox is being unchecked after being checked, mark as touched immediately
        if (!newValue && this.termsWasChecked) {
          this.termsGroupTouched = true;
        }

        // Track if checkbox has been checked
        if (newValue) {
          this.termsWasChecked = true;
        }
      }
    }
  }

  onTermsCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    // If checkbox is being unchecked after being checked, mark as touched immediately
    if (!isChecked && this.termsWasChecked) {
      this.termsGroupTouched = true;
    }

    // Track if checkbox has been checked
    if (isChecked) {
      this.termsWasChecked = true;
    }

    // Update form value
    this.form.get('termsAccepted')?.setValue(isChecked);
  }

  waitForFormAndSubmit = () => {
    this.$timeout(() => {
      this.onSubmit();
    });
  };
}
